import { formatDate } from "utils/dateFormatter";
import { dynamicModules } from "config/dynamicModules";
import { mapModuleProps } from "helpers/mapModuleProps";


/**
 * Gets the route for a given module ID from dynamicModules config
 * 
 * @param {number} moduleId - The module ID to lookup
 * @returns {string} The route string for the module
 */
const getRouteByModuleId = (moduleId) => {
	const mod = dynamicModules.find(m => m.module_id === moduleId);
	return mod ? mod.route : "";
};

/**
 * Maps module data into structured page objects with sections based on module type
 * Handles both list views and detail views for various module types
 * 
 * @param {number} module_id - The module ID determining the structure pattern
 * @param {Object|Array} module_data - The module data to map
 * @param {string} routePrefix - Optional prefix for routes
 * @param {string} baseRoute - Base route path
 * @param {string} pageRoute - Current page route
 * @param {Object} options - Additional options for mapping
 * @param {boolean} options.isModuleSubdomain - Whether viewing on a module-specific subdomain
 * @param {boolean} options.usePathModule - Whether using pathModuleType in dev mode
 * @param {boolean} options.isStructureOnly - Whether to return only the structure without data
 * @param {string} options.cartToken - Optional cart token for checkout
 * @returns {Object} The mapped page object with sections
 */
export const mapDynamicPageModules = (module_id, module_data, routePrefix = "", baseRoute = "", pageRoute = "", options = {}) => {
	let page = { sections: [] };
	if (!module_data || typeof module_data?.data !== "object") return page;

	module_data = module_data.data;

	// Get route centrally
	const route = getRouteByModuleId(module_id);
	
	// Extract options
	const { isModuleSubdomain = false, usePathModule = false, showListView = false, cartToken = null } = options;

	/**
	 * Creates a proper link based on subdomain context
	 * 
	 * @param {string} uniqueUrl - The unique URL identifier
	 * @param {string} targetSubdomain - Optional target subdomain for cross-domain links
	 * @param {number} targetModuleId - Optional module ID for cross-module links
	 * @returns {string} The proper URL for the current context
	 */
	const createProperLink = (uniqueUrl, targetSubdomain = null, targetModuleId = null) => {
		// Get the route for the target module if specified
		const targetRoute = targetModuleId ? getRouteByModuleId(targetModuleId) : route;
		
		// IMPORTANT: Instead of returning empty string, return a meaningful default
		if (!uniqueUrl) {
			// For missing uniqueUrl, create a link to the module overview
			const orgUrl = window.location.pathname.split('/')[1] || 'engbers-bau';
			
			// For subdomain mode: /{orgUrl}
			if (isModuleSubdomain || usePathModule) {
				return `/${orgUrl}`;
			}
			// For normal website context: /{targetRoute}
			return `/${targetRoute}`;
		}
		
		// Check if we're already on the target subdomain
		const currentSubdomain = window.location.hostname.split('.')[0];
		const effectiveTargetSubdomain = (currentSubdomain === targetSubdomain) ? null : targetSubdomain;
		
		// Extract organization from pathname
		const orgUrl = window.location.pathname.split('/')[1] || '';
		
		// For subdomain context or DEV mode, use the module route as part of the path
		if (isModuleSubdomain || usePathModule) {
			// If linking to different subdomain, create absolute URL with direct org/route/uniqueUrl format
			if (effectiveTargetSubdomain) {
				return `https://${effectiveTargetSubdomain}.canou.io/${orgUrl}/${targetRoute ? targetRoute + '/' : ''}${uniqueUrl}`;
			}
			// For links on the same subdomain or DEV mode, include the target module route in the path
			return `/${orgUrl}/${targetRoute ? targetRoute + '/' : ''}${uniqueUrl}`;
		}
		
		// For regular site context, include the target route
		return `/${targetRoute}/${uniqueUrl}`;
	};

	/**
	 * Creates consistent module item links for list views
	 * Using mapModuleProps but ensuring consistent cta_links
	 * 
	 * @param {Array} items - Module items to process
	 * @returns {Array} Items with consistent cta_links
	 */
	const processModuleItems = (items, moduleId) => {
		if (!Array.isArray(items)) return [];
		
				// Direct processing for forms to ensure buttons are set correctly
		if (moduleId === 9010) { // Forms
			return items.map(item => ({
				id: item.id || Math.random().toString(36).substring(2, 9),
				title: item.title,
				short_desc: item.short_desc,
				desc: item.desc,
				cta_text: "Zum Formular", // Explicitly set
				cta_link: createProperLink(item.unique_url), // Directly create link
				media: item.medias || item.media,
				unique_url: item.unique_url
			}));
		}
		
		// Standard processing for other module types
		const mappedItems = mapModuleProps(moduleId, items, routePrefix, baseRoute, null, {
			isModuleSubdomain, usePathModule, showListView
		});
		
		// Then ensure each item has consistent cta_link using our helper
		return mappedItems.map(item => {
			// Extract uniqueUrl from the item or from original data
			const uniqueUrl = item.unique_url || 
				items.find(orig => orig.title === item.title)?.unique_url;
				
			return {
				...item,
				// Always ensure cta_text exists
				cta_text: item.cta_text || (moduleId === 9010 ? "Zum Formular" : "Details"),
				// Re-generate consistent cta_link
				cta_link: uniqueUrl ? createProperLink(uniqueUrl) : createProperLink(null)
			};
		});
	};

	/**
	 * Filters sections based on display rules for subdomains
	 * 
	 * @param {Array} sections - The sections to filter
	 * @returns {Array} Filtered sections
	 */
	const filterSections = (sections) => {
		// Only filter when called on subdomain
		if (isModuleSubdomain) {
			return sections.filter(section => !section.hideOnModuleSubdomain);
		}
		return sections;
	};

	switch (module_id) {
		case 9999: // Checkout
					// If cartToken is present, load a specific order
					page = {
						route: route,
						cartToken: cartToken,
						sections: [
							// {
							// 	global_type: { id: 95, name: "BreadcrumbsSection" },
							// 	title: checkoutTitle,
							// 	fluid: 1,
							// 	hideOnModuleSubdomain: true,
							// 	margin_y: 1,
							// 	is_absolute: false,
							// },
							{
								global_type: { id: 1000, name: "CheckoutSection" },
								data: module_data,
								cartToken: cartToken,
								fluid: 1,
							},
						]
					};
					page.sections = filterSections(page.sections);
					return page;


		case 1380: // Legal Notice
			page = {
				title: "Impressum",
				desc: "In unserem Impressum finden Sie alle rechtlichen Informationen, Verantwortlichkeiten und Kontaktmöglichkeiten zu unserem Unternehmen",
				route: route,
				sections: [
					{
						global_type: { id: 95, name: "BreadcrumbsSection" },
						title: "Impressum",
						hideOnModuleSubdomain: true,
						padding_y: 4,
					},
					{
						global_type: { id: 1, name: "HeroSection" },
						title: "Impressum",
						hideOnModuleSubdomain: true 
					},
					{
						global_type: { id: 11, name: "FeatureSection" },
						desc: module_data[0]?.desc || "<p>Impressumsinformationen werden geladen...</p>"
					},
					{
						global_type: { id: 91, name: "FooterSection" },
						order: "forward",
						alignment: "start",
						text_color_title: "#fff",
						bg_color: "var(--bs-primary)",
						hideOnModuleSubdomain: true
					}
				]
			};
			page.sections = filterSections(page.sections);
			return page;

		case 1390: // Privacy Policy
			page = {
				title: "Datenschutz",
				desc: "Unsere Datenschutzerklärung. Die Datenschutzerklärung informiert über die Erhebung, Verarbeitung und Nutzung Ihrer Daten.",
				route: route,
				sections: [
					{
						global_type: { id: 95, name: "BreadcrumbsSection" },
						title: "Datenschutz",
						hideOnModuleSubdomain: true,
						padding_y: 4,
					},
					{
						global_type: { id: 1, name: "HeroSection" },
						title: "Datenschutz",
						hideOnModuleSubdomain: true
					},
					{
						global_type: { id: 11, name: "FeatureSection" },
						desc: module_data[0]?.desc || "<p>Datenschutzinformationen werden geladen...</p>"
					},
					{
						global_type: { id: 91, name: "FooterSection" },
						order: "forward",
						alignment: "start",
						text_color_title: "#fff",
						bg_color: "var(--bs-primary)",
						hideOnModuleSubdomain: true
					}
				]
			};
			page.sections = filterSections(page.sections);
			return page;

		case 4650: // Feedback
			page = {
				title: "Feedback",
				route: route,
				sections: [
					{
						global_type: { id: 95, name: "BreadcrumbsSection" },
						title: "Feedback",
						hideOnModuleSubdomain: true,
						padding_y: 4,
					},
					{
						global_type: { id: 111, name: "FormSection" },
						title: "Ihre Meinung ist uns wichtig",
						col_width: 8,
						form: {
							"id": 5,
							"unique_url": null,
							"lead_suffix": "feedback/leads/public",
							"title": "Teilen Sie Ihre Erfahrungen mit uns",
							"short_desc": "Wir schätzen Ihr Feedback sehr und nutzen es, um unsere Dienstleistungen kontinuierlich zu verbessern. Bitte teilen Sie Ihre Gedanken, Vorschläge oder Kommentare mit uns.",
							"desc": null,
							"policy_text": null,
							"type_id": 2,
							"tags": null,
							"media": null,
							"lead_contact": 2,
							"contact_details_first": 1,
							"enable_anonymous_option": 1,
							"steps": [
								{
									"id": null,
									"form_id": null,
									"sort_number": 0,
									"media": null,
									"title": "Ihr Feedback",
									"desc": null,
									"short_desc": null,
									"type": 3,
									"min_count": 0,
									"max_count": 0,
									"public": 1,
									"active": 1,
									"required": 1,
									"medias": [],
									"items": [
										{
											"id": null,
											"form_step_id": null,
											"title": "desc",
											"short_desc": null,
											"desc": null,
											"placeholder": "Bitte auswählen...",
											"label": "Bereich",
											"options": [
												{
													"label": "Finanzen",
													"value": 1
												},
												{
													"label": "Sicherheit",
													"value": 2
												},
												{
													"label": "Umwelt",
													"value": 3
												},
												{
													"label": "Personal",
													"value": 4
												},
												{
													"label": "Produktion",
													"value": 5
												}

											],
											"media": null,
											"type": 11,
											"max_char": 0,
											"sort_number": 0,
											"score": null,
											"public": 1,
											"active": 1,
											"required": 1,
											"serialize_in_top_level": 1,
											"top_level_key": "area_id"
										},
										{
											"id": null,
											"form_step_id": null,
											"title": "desc",
											"short_desc": null,
											"desc": null,
											"placeholder": "Bitte auswählen...",
											"label": "Dringlichkeit",
											"options": [
												{
													"label": "Niedrig",
													"value": 5
												},
												{
													"label": "Mittel",
													"value": 4
												},
												{
													"label": "Hoch",
													"value": 3
												},
												{
													"label": "Sehr hoch",
													"value": 2
												},
												{
													"label": "Kritisch",
													"value": 1
												}
											],
											"media": null,
											"type": 11,
											"max_char": 0,
											"sort_number": 0,
											"score": null,
											"public": 1,
											"active": 1,
											"required": 1,
											"serialize_in_top_level": 1,
											"top_level_key": "level_id"
										},
										{
											"id": null,
											"form_step_id": null,
											"title": "title",
											"short_desc": null,
											"desc": null,
											"placeholder": "Bitte geben Sie einen aussagekräftigen Betreff ein",
											"label": "Worum geht es bei Ihrem Feedback?",
											"options": [],
											"media": null,
											"type": 1,
											"max_char": 0,
											"sort_number": 0,
											"score": null,
											"public": 1,
											"active": 1,
											"required": 1,
											"serialize_in_top_level": 1,
											"top_level_key": "title"
										},
										{
											"id": null,
											"form_step_id": null,
											"title": "desc",
											"short_desc": null,
											"desc": null,
											"placeholder": "Beschreiben Sie Ihre Erfahrungen, Vorschläge oder Fragen im Detail...",
											"label": "Ihr detailliertes Feedback",
											"options": [],
											"media": null,
											"type": 8,
											"max_char": 0,
											"sort_number": 0,
											"score": null,
											"public": 1,
											"active": 1,
											"required": 1,
											"serialize_in_top_level": 1,
											"top_level_key": "desc"
										},
										
									]
								}
							],
							"global_lead_contact": [],
							"medias": [],
							"type": {
								"id": 2,
								"title": "Untereinander",
								"short_desc": "Die Schritte werden untereinander auf einer einzelnen Seite angezeigt.",
								"desc": null,
							},
							"globalLeadContact": []
						}
					},
				]
			};
			page.sections = filterSections(page.sections);
			return page;

		case 1610: // Events
			if (Array.isArray(module_data)) {
				// Find event module configuration	
						
				// List page for events - setup structure only, data will be loaded in Section
				page = {
					title: "Veranstaltungen",
					route: route,
					sections: [
						{
							global_type: { id: 95, name: "BreadcrumbsSection" },
							title: "Veranstaltungen",
							hideOnModuleSubdomain: true,
							padding_y: 4,
						},
						{
							global_type: { id: 52, name: "CardListSection" },
							global_view: { id: 3 },
							title: "Kommende Veranstaltungen",
							// Mark for self-loading with infinite scroll
							shouldLoadOwnData: true,
							endpoint: "events",
							module_id: 1610,
							items_per_line: 1,
						}
					]
				};
			} else {
				const mappedData = mapModuleProps(module_id, [module_data], "" ,"")?.[0] ??  {};

				let formPrefix = "/formulare/";
			
				// For subdomain calls, generate external link to forms subdomain
				if (isModuleSubdomain) {
					const orgUrl = window.location.pathname.split('/')[1];
					formPrefix = `https://forms.canou.io/${orgUrl}/`;
				}

				page = {
					title: module_data.title || "Veranstaltung",
					route: route,
					sections: [
						{
							global_type: { id: 95, name: "BreadcrumbsSection" },
							title: module_data.title,
							hideOnModuleSubdomain: true,
							padding_y: 4,
						},
						{
							global_type:{ id: 1, name: "HeroSection" },
							title: module_data.title,
							desc: module_data.short_content,
							title_vertical_alignment: "center",
							media: module_data.medias || module_data.media || [],
							cta_text: module_data.form?.unique_url ? "Zur Anmeldung": null,
							cta_link: module_data.form?.unique_url ? `${formPrefix}${module_data.form?.unique_url}` : null,

							alignment : "start",
							alignment_content: "end",
							alignment_title: "start",
							padding_y: 8,
							col_width: 12,
							col_width_content: 5,
							col_width_title: 6,
							
							vertical_alignment_title: "start",
							order: "forward",
							text_alignment_title: "start",
							vertical_alignment_content: "center",

						},
						{
							global_type:{ id: 16, name: "DetailsSection" },
							global_view: {id: 1},
							list: mappedData.simple_feature_list,

							sort_number: 1,
							alignment: "center",
							col_width: 12,
							col_width_title: 12,
							col_width_content: 12,
							alignment_title: "start",
							alignment_content: "start",
							vertical_alignment_title: "start",
							order: "forward",
							text_color_title: null,
							text_alignment_title: "start",
							vertical_alignment_content: "start",
							padding_y: "0",
							padding_x: null,
							margin_y: "0",
							margin_x: null,
							max_count: 0,
						},
						{
							global_type:{ id: 16, name: "DetailsSection" },
							global_view: {id: 2},
							list: mappedData.list,

							sort_number: 2,
							alignment: "center",
							col_width: 12,
							col_width_title: 12,
							col_width_content: 12,
							alignment_title: "start",
							alignment_content: "start",
							vertical_alignment_title: "start",
							order: "forward",
							text_color_title: null,
							text_alignment_title: "start",
							vertical_alignment_content: "start",
							padding_y: "0",
							padding_x: null,
							margin_y: "0",
							margin_x: null,
							max_count: 0,
						},
					]
				};
				if (Array.isArray(module_data.stages) && module_data.stages.length > 0) {
					module_data.stages.forEach ((stage) => {
						if (Array.isArray(stage.agendas) && stage.agendas.length > 0) {
							const mappedAgendas = mapModuleProps(4910, stage.agendas ) ?? null;

							if (mappedAgendas) {
								page.sections.push({
									global_type:{ id: 41, name: "TimelineSection" },
									title: stage.title ?? null,
									desc: stage.desc ?? null,
									data: mappedAgendas,
								})

							}
						}
					});
				}

				
				if (mappedData.desc) page.sections.push({
					global_type:{ id: 11, name: "FeatureSection" },
					title: "Was Sie erwartet",
					desc: mappedData.desc,

				})

				if (Array.isArray(mappedData.speakers) && mappedData.speakers.length > 0) page.sections.push({
					global_type: { id: 51, name: "CardsSection" },
					global_view: {id: 1},
					items_per_line: 4,
					tablet_items_per_line: 3,
					mobile_items_per_line: 2,
					title: "Diese Sprecher werden dabei sein",
					data: mappedData.speakers,
					desc: "Diese Referenten sind Experten auf ihrem Gebiet und werden Sie mit ihren innovativen Ideen und praxisorientierten Tipps inspirieren."
				})
				
			}
			page.sections = filterSections(page.sections);
			return page;

		case 9010: // Forms
			if (Array.isArray(module_data)) {
				
				// Standard processing for all modules - no special treatment anymore
				const mappedForms = mapModuleProps(module_id, module_data, routePrefix, baseRoute, null, {
					isModuleSubdomain, usePathModule, showListView
				});
				
				page = {
					title: "Formulare",
					route: route,
					sections: [
						{
							global_type: { id: 95, name: "BreadcrumbsSection" },
							title: "Formulare",
							hideOnModuleSubdomain: true,
							padding_y: 4,
						},
						{
							global_type: { id: 52, name: "CardsListSection" },
							title: "Formulare",
							shouldLoadOwnData: false,
							endpoint: "forms",
							module_id: 9010,
							loading: false,
							data: mappedForms
						}
					]
				};
			} else {
				// Detail page
				page = {
					title: module_data.title || "Formular",
					route: route,
					sections: [
						{
							global_type: { id: 95, name: "BreadcrumbsSection" },
							title: module_data.title,
							hideOnModuleSubdomain: true,
							padding_y: 4,
						},
						{
							global_type: { id: 111, name: "FormSection" },
							col_width: 12,
							col_width_title: 12,
							col_width_content: 8,
							alignment: "center",
							alignment_title: "center",
							alignment_content: "center",
							text_alignment_content: "start",
							policy_text: module_data.policy_text,
							padding_x: 6,
							padding_y: 1,
							form: module_data,
						}
					]
				};
			}
			page.sections = filterSections(page.sections);
			return page;

		case 1325: // Links
			// Detail page only (hasListView: false)
			console.log("Link module data:", module_data);
			page = {
				title: module_data.title || "Link",
				route: route,
				sections: [
					// {
					// 	global_type: { id: 95, name: "BreadcrumbsSection" },
					// 	title: module_data.title,
					// 	hideOnModuleSubdomain: true,
					// 	padding_y: 4,
					// },
					{
						global_type: { id: 131, name: "RedirectSection" },
						title: module_data.title,
						subtitle: module_data.short_desc,
						desc: module_data.desc,
						url: module_data.url,
					}
				]
			};
			page.sections = filterSections(page.sections);
			return page;

		case 2610: // Job Offers
			if (Array.isArray(module_data)) {
				// If only one item, treat as detail page
				if (module_data.length === 1) {
					module_data = module_data[0];
				} else {
					// List page - apply the consistent link generator
					const mappedJobs = processModuleItems(module_data, module_id);
					
					// List page
					page = {
						title: "Stellenangebote",
						route: route,
						sections: [
							{
								global_type: { id: 95, name: "BreadcrumbsSection" },
								title: "Stellenangebote",
								hideOnModuleSubdomain: true,
								padding_y: 4
							},
							{
								global_type: { id: 52, name: "CardsListSection" },
								title: "Stellenangebote",
								shouldLoadOwnData: true,
								endpoint: "job/offers",
								module_id: 2610,
								items_per_page: 12,
								data: mappedJobs
							}
						]
					};
					page.sections = filterSections(page.sections);
					return page;
				}
			}
			
			// Adjust form link based on context
			let formularLink = createProperLink(module_data.form?.unique_url, null, 9010);
			
			// Detail page
			page = {
				title: module_data.title || "Stellenangebot",
				route: route,
				sections: [
					{
						global_type: { id: 95, name: "BreadcrumbsSection" },
						title: module_data.title || "",
						back_link: route,
						back_link_text: "Stellenangebote",
						hideOnModuleSubdomain: true,
						padding_y: 4,
					},
					{
						global_type: { id: 1, name: "HeroSection" },
						title: module_data.title || "",
						short_desc: module_data.short_desc || "",
						title_vertical_alignment: "center",
						padding_y: 6,
					},
					{
						global_type: { id: 16, name: "DetailsSection" },
						title: "Werden Sie Teil des Teams",
						alignment: "center",
						media: module_data.medias || module_data.media || [],
						padding_y: 3,
						col_width: 10,
						col_width_title: 12,
						col_width_content: 12,
						text_alignment_title: "center",
						text_alignment_content: "left",
						list: {
							title: "Details",
							items: [
								{
									title: "Stellenangebot",
									value: module_data.title,
									icon: "briefcase"
								},
								{
									title: "Starttermin",
									value: module_data.immediately ? "sofort" : module_data.valid_until ? formatDate(module_data.valid_until) : null,
									icon: "calendarAlt"
								},
								{
									title: "Beschäftigungsart",
									value: module_data.employment_type?.name,
									icon: "clock"
								},
								{
									title: "Standort",
									value: [
										module_data.address_title,
										module_data.address_street,
										[
											module_data.address_postal_code, 
											module_data.address_location
										].filter(Boolean).join(" "),
										module_data.address_region,
										module_data.address_country
									].filter(Boolean).join("<br/>"),
									icon: "locationDot"
								}
							]
						}
					},
					{
						global_type: { id: 11, name: "FeatureSection" },
						desc: 	[
									module_data.desc,
									module_data.tasks,
									module_data.benefits,
									module_data.skills,
									module_data.extra_desc
								].filter(part => part != null && part !== undefined && part !== "").join('<hr class="my-5 border-primary">'),
						col_width: 8,
						cta_text: "Jetzt Bewerben",
						cta_link: formularLink
					}
				]
			};
			page.sections = filterSections(page.sections);
			return page;

		default:
			page = {
				title: module_data.title || "",
				sections: module_data.sections || []
			};
			
			// Add properties to existing sections
			if (page.sections && Array.isArray(page.sections)) {
				page.sections = page.sections.map(section => {
					// Add hideOnModuleSubdomain to breadcrumbs and hero sections
					if (section.global_type?.id === 95 || section.global_type?.id === 1) {
						return { ...section, hideOnModuleSubdomain: true };
					}
					
					return section;
				});
			}
			
			page.sections = filterSections(page.sections);
			return page;
	}
};
